<template>
  <InsightsTableContainer
    tooltip-key="data-table-general"
    download-id="outbound-report-table"
    :error="error"
    class="table"
    :empty-data="emptyData || noWidgetData"
    :loading="loading"
    label="Outbound Sources Data Table"
    :headers="tableHeaders"
    :downloading="downloading"
    @download-csv="$emit('download-csv')"
  >
    <template #table>
      <InsightsTable
        :headers="tableHeaders"
        :rows="openRows.includes(0) || !useCollapsibleRows
          ? tablesSorted[0]
          : [tablesSorted[0][0]]
        "
        :totals="tableTotals"
        class="pa-4 pt-0"
        :class="{
          'pb-0': !openRows.includes(1) && !openRows.includes(0) && useCollapsibleRows,
        }"
        show-passthrough-header
      >
        <template #total:label>
          <span class="pa-2 new-h3 grand-total-label">
            Total
          </span>
        </template>
        <template #total:cell="{ total }">
          <PassthroughCell
            v-if="total.isPassthrough"
            :value="total.value"
            :percent="total.percent"
            class="grand-total-cell"
          />
          <div v-else class="new-body-text grand-total-cell">
            {{ formatValue(total.value) }}
          </div>
        </template>
        <template #[`row:label:${firstCategoryKey}`]>
          <span
            class="category-label"
            :class="{ 'cursor-pointer': useCollapsibleRows }"
            @click="handleHeaderClick(0)"
          >
            {{ firstCategoryLabel }}
            <v-icon
              v-if="useCollapsibleRows"
              :icon="openRows.includes(0)
                ? '$chevron-up'
                : '$chevron-down'
              "
            />
          </span>
        </template>
        <template #[`row:cell:${firstCategoryKey}`]="{ item, key }">
          <InsightsTableCell
            v-if="!TABLE_KEYS_TO_EXCLUDE.includes(key)"
            class="category-cell"
            :class="{ 'cursor-pointer': useCollapsibleRows }"
            :item="item"
            :item-key="key"
            @click="handleHeaderClick(0)"
          />
        </template>
      </InsightsTable>
      <InsightsTable
        :headers="openRows.includes(1) || !useCollapsibleRows
          ? tableHeaders
          : []
        "
        :rows="openRows.includes(1) || !useCollapsibleRows
          ? tablesSorted[1]
          : [tablesSorted[1][0]]
        "
        class="pa-4 pt-0"
        show-passthrough-header
      >
        <template #[`row:label:${secondCategoryKey}`]>
          <span
            class="category-label"
            :class="{ 'cursor-pointer': useCollapsibleRows }"
            @click="handleHeaderClick(1)"
          >
            {{ secondCategoryLabel }}
            <v-icon
              v-if="useCollapsibleRows"
              :icon="openRows.includes(1)
                ? '$chevron-up'
                : '$chevron-down'
              "
            />
          </span>
        </template>
        <template #[`row:cell:${secondCategoryKey}`]="{ item, key }">
          <InsightsTableCell
            v-if="!TABLE_KEYS_TO_EXCLUDE.includes(key)"
            class="category-cell"
            :class="{ 'cursor-pointer': useCollapsibleRows }"
            :item="item"
            :item-key="key"
            @click="handleHeaderClick(1)"
          />
        </template>
      </InsightsTable>
    </template>
  </InsightsTableContainer>
</template>

<script setup>
import InsightsTableContainer from '@/components/insights/table/InsightsTableContainer.vue';
import InsightsTable from '@/components/insights/table/InsightsTable.vue';
import InsightsTableCell from '@/components/insights/table/InsightsTableCell.vue';
import PassthroughCell from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/PassthroughCell.vue';
import { formatValue, TABLE_KEYS_TO_EXCLUDE } from '@/components/insights/helpers';
import { computed, ref } from 'vue';

const props = defineProps({
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
  agencyRows: {
    type: Array,
    default: () => [],
  },
  prospectingRows: {
    type: Array,
    default: () => [],
  },
  tableTotals: {
    type: Array,
    default: () => [],
  },
  downloading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['download-csv']);

const openRows = ref([]);

const noWidgetData = computed(() => !props.prospectingRows.length && !props.agencyRows.length);

const tableHeaders = [
  'Applied',
  'Screening',
  'Assessment',
  'Offer',
  'Hired',
];

const tablesSorted = computed(() => [
  props.agencyRows,
  props.prospectingRows,
].sort((a, b) => (b?.[0]?.review?.value || 0) - (a?.[0]?.review?.value || 0)));

const useCollapsibleRows = computed(() => tablesSorted.value.some((table) => table?.length > 10));

const firstCategoryKey = computed(() => tablesSorted.value[0]?.[0]?.id);
const secondCategoryKey = computed(() => tablesSorted.value[1]?.[0]?.id);

const firstCategoryLabel = computed(() => tablesSorted.value[0]?.[0]?.label || '');
const secondCategoryLabel = computed(() => tablesSorted.value[1]?.[0]?.label || '');

const handleHeaderClick = (index) => {
  if (!useCollapsibleRows.value) return;

  if (openRows.value.includes(index)) {
    openRows.value = openRows.value.filter((i) => i !== index);
  } else {
    openRows.value.push(index);
  }
};
</script>

<style scoped lang="less">
.category-label {
  font-weight: bold;
}

.category-cell, .category-cell :deep(span) {
  font-weight: bold;
}

.grand-total-label {
  color: @text-brand;
}

.grand-total-cell, .grand-total-cell :deep(span) {
  color: @text-brand;
}

:deep(.totals) .table-cell:not(.spacing) {
  background: fade(@bg-brand-default, 5%);
}
</style>
